import { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { Link } from 'react-router-dom';
import { MdCloudUpload, MdDownload } from 'react-icons/md';
import { useUser } from '../../context/UserContext';
import { recapturaCarga } from '../../api/apiConfig';
import { CATEGORIAS, unwrap } from './recapturaUtils';

const descargarPlantillaEjemplo = () => {
  const wb = XLSX.utils.book_new();
  const hoja = XLSX.utils.aoa_to_sheet([
    ['RUT', 'CATEGORIA', 'EJECUTIVO', 'COMPETIDOR', 'MOTIVO_MGC', 'CLIENTE'],
    [
      '76.321.450-1',
      'Sin operar +3M',
      'Carolina Rojas',
      'Banco Cordillera',
      'Tasa / precio',
      'Transportes Andes SpA',
    ],
  ]);
  XLSX.utils.book_append_sheet(wb, hoja, 'RECAPTURA');
  XLSX.writeFile(wb, 'plantilla_recaptura_ejemplo.xlsx');
};

const normalizeHeader = (v) => String(v ?? '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toUpperCase()
  .replace(/[_\-.]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const findHeader = (headers, aliases) => {
  const set = new Set(aliases.map(normalizeHeader));
  return headers.find((h) => set.has(normalizeHeader(h))) || null;
};

const normalizeRut = (value) => {
  const raw = String(value ?? '').toUpperCase().replace(/[^0-9K]/g, '');
  if (raw.length < 8 || raw.length > 9) return null;
  const cuerpo = raw.slice(0, -1);
  const dv = raw.slice(-1);
  if (!/^\d+$/.test(cuerpo)) return null;
  return `${Number(cuerpo)}-${dv}`;
};

const normalizeCategoria = (value) => {
  const key = String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
  if (key.includes('PENETR') || (key.includes('30') && key.includes('PEN'))) return CATEGORIAS[0];
  if (key.includes('SINOPERAR') || (key.includes('3M') && key.includes('SIN'))) return CATEGORIAS[1];
  if (key.includes('NUNCA')) return CATEGORIAS[2];
  const exact = CATEGORIAS.find((c) => c.toUpperCase().replace(/[^A-Z0-9]/g, '') === key);
  return exact || null;
};

const RecapturaCarga = () => {
  const { user } = useUser();
  const [nombre, setNombre] = useState('');
  const [fecIni, setFecIni] = useState('');
  const [fecFin, setFecFin] = useState('');
  const [fileName, setFileName] = useState('');
  const [validRows, setValidRows] = useState([]);
  const [invalidRows, setInvalidRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const canSubmit = useMemo(
    () => !!user && !!nombre.trim() && !!fecIni && !!fecFin && validRows.length > 0,
    [user, nombre, fecIni, fecFin, validRows],
  );

  const onFile = async (file) => {
    if (!file) return;
    setFileName(file.name);
    setResult(null);
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const sheet = wb.Sheets[wb.SheetNames[0]];
    const json = XLSX.utils.sheet_to_json(sheet, { defval: '' });
    if (!json.length) {
      setValidRows([]);
      setInvalidRows([{ row: 0, error: 'Archivo vacío' }]);
      return;
    }
    const headers = Object.keys(json[0]);
    const hRut = findHeader(headers, ['RUT', 'RUT CLIENTE', 'RUT_CLI']);
    const hCat = findHeader(headers, ['CATEGORIA', 'CATEGORÍA', 'CATEGORIA CAMPANA', 'SEGMENTO']);
    const hEjec = findHeader(headers, ['EJECUTIVO', 'EJEC']);
    const hComp = findHeader(headers, ['COMPETIDOR', 'COMPETENCIA']);
    const hMot = findHeader(headers, ['MOTIVO MGC', 'MOTIVO_MGC', 'MOTIVO']);
    const hCli = findHeader(headers, ['CLIENTE', 'RAZON SOCIAL', 'NOMBRE']);

    const ok = [];
    const bad = [];
    const seen = new Set();
    json.forEach((row, idx) => {
      const rut = normalizeRut(hRut ? row[hRut] : '');
      const cat = normalizeCategoria(hCat ? row[hCat] : '');
      if (!rut) {
        bad.push({ row: idx + 2, error: 'rut_invalido', raw: hRut ? row[hRut] : '' });
        return;
      }
      if (!cat) {
        bad.push({ row: idx + 2, rut, error: 'categoria_invalida', raw: hCat ? row[hCat] : '' });
        return;
      }
      if (seen.has(rut)) {
        bad.push({ row: idx + 2, rut, error: 'duplicado_archivo' });
        return;
      }
      seen.add(rut);
      ok.push({
        rut,
        categoria: cat,
        ejecutivo: hEjec ? String(row[hEjec] || '').trim() || null : null,
        competidor: hComp ? String(row[hComp] || '').trim() || null : null,
        motivo_mgc: hMot ? String(row[hMot] || '').trim() || null : null,
        cliente: hCli ? String(row[hCli] || '').trim() || null : null,
      });
    });
    setValidRows(ok);
    setInvalidRows(bad);
  };

  const submit = async (mode) => {
    if (!canSubmit && mode !== 'test') {
      window.alert('Completa nombre, fechas y un Excel con filas válidas.');
      return;
    }
    if (!nombre.trim() || !fecIni || !fecFin) {
      window.alert('Completa nombre y fechas.');
      return;
    }
    if (validRows.length === 0) {
      window.alert('No hay filas válidas.');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const resp = unwrap(
        await recapturaCarga(user, {
          mode,
          nombre: nombre.trim(),
          fec_ini: fecIni,
          fec_fin: fecFin,
          rows: validRows,
          desactivar_anteriores: true,
        }),
      );
      setResult(resp);
      if (!resp.ok) throw new Error(resp.error || 'Error en carga');
      window.alert(`Carga OK: ${resp.inserted} insertados (campaña #${resp.campana_id}).`);
    } catch (e) {
      window.alert(e.message || 'No se pudo cargar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-2xl font-bold text-gray-800">Activación Comercial — Carga Excel</h1>
        <p className="text-sm text-gray-600">Inserta RUTs en `MGC_dev` (no productivo)</p>
        <Link to="/recaptura/matriz" className="btn-primary text-sm py-2 px-3" style={{ textDecoration: 'none' }}>
          Ver matriz
        </Link>
      </div>

      <div className="seccion-datos grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nombre campaña</label>
          <input className="w-full px-3 py-2 border rounded-lg text-sm" value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Activación Comercial Mar 2026" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Fecha inicio</label>
          <input type="date" className="w-full px-3 py-2 border rounded-lg text-sm" value={fecIni} onChange={(e) => setFecIni(e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Fecha fin</label>
          <input type="date" className="w-full px-3 py-2 border rounded-lg text-sm" value={fecFin} onChange={(e) => setFecFin(e.target.value)} />
        </div>
      </div>

      <div className="seccion-datos">
        <p className="text-sm text-gray-600 mb-3">
          Columnas obligatorias: <b>RUT</b>, <b>CATEGORIA</b> ({CATEGORIAS.join(' | ')}).
          Opcionales: EJECUTIVO, COMPETIDOR, MOTIVO_MGC, CLIENTE.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="inline-flex items-center gap-2 text-sm py-2 px-3 border rounded-lg bg-white"
            onClick={descargarPlantillaEjemplo}
          >
            <MdDownload /> Descargar Excel de ejemplo
          </button>
          <label className="inline-flex items-center gap-2 btn-primary text-sm py-2 px-3 cursor-pointer">
            <MdCloudUpload /> Seleccionar Excel
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={(e) => onFile(e.target.files?.[0])}
            />
          </label>
          {fileName && <span className="text-sm text-gray-600">{fileName}</span>}
        </div>

        <div className="mt-4 flex flex-wrap gap-3 text-sm">
          <span className="text-green-700 font-semibold">Válidas: {validRows.length}</span>
          <span className="text-red-700 font-semibold">Inválidas: {invalidRows.length}</span>
        </div>

        {validRows.length > 0 && (
          <div className="mt-3 overflow-auto max-h-56 border rounded-lg">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-100 sticky top-0">
                <tr>
                  <th className="p-2 text-left">RUT</th>
                  <th className="p-2 text-left">Categoría</th>
                  <th className="p-2 text-left">Ejecutivo</th>
                  <th className="p-2 text-left">Competidor</th>
                </tr>
              </thead>
              <tbody>
                {validRows.slice(0, 50).map((r) => (
                  <tr key={r.rut} className="border-t">
                    <td className="p-2">{r.rut}</td>
                    <td className="p-2">{r.categoria}</td>
                    <td className="p-2">{r.ejecutivo || '(desde sf_lineas)'}</td>
                    <td className="p-2">{r.competidor || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {invalidRows.length > 0 && (
          <div className="mt-3 text-xs text-red-700 max-h-40 overflow-auto">
            {invalidRows.slice(0, 30).map((r, i) => (
              <div key={i}>Fila {r.row}: {r.error} {r.rut || r.raw || ''}</div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-2 mt-4">
          <button type="button" className="btn-primary text-sm" disabled={loading || !canSubmit} onClick={() => submit('full')}>
            {loading ? 'Cargando…' : 'Cargar campaña'}
          </button>
          <button type="button" className="text-sm py-2 px-3 border rounded-lg bg-white" disabled={loading || validRows.length === 0} onClick={() => submit('test')}>
            Prueba (5 filas)
          </button>
        </div>

        {result && (
          <pre className="mt-4 text-xs bg-slate-50 p-3 rounded-lg overflow-auto">{JSON.stringify(result, null, 2)}</pre>
        )}
      </div>
    </div>
  );
};

export default RecapturaCarga;
